import { CommonModule, Location } from '@angular/common';
import { Component, Input, OnInit, computed, inject, signal } from '@angular/core';
import { DomSanitizer, type SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideArrowLeft, lucideClipboard, lucideEye, lucideMessageSquare, lucideRefreshCw, lucideSend, lucideX } from '@ng-icons/lucide';

import { UI, type UiBadgeVariant } from '../../components/ui';
import type { EntityConfig } from '../../config/entity-config';
import { MessageQueueService, type MessageQueueItem } from '../../services/message-queue.service';
import { ShellRoutesService } from '../../services/shell-routes.service';
import { goBackOrNavigate } from '../../utils/navigation';

/**
 * Detail page for a single MessageQueue item (SMS / email): status, routing, body, last error,
 * per-attempt history and delivery confirmation. Secrets and OTPs are redacted by the API.
 */
@Component({
  selector: 'app-message-queue-detail',
  standalone: true,
  imports: [CommonModule, NgIcon, ...UI],
  templateUrl: './message-queue-detail.component.html',
  viewProviders: [provideIcons({ lucideArrowLeft, lucideClipboard, lucideEye, lucideMessageSquare, lucideRefreshCw, lucideSend, lucideX })],
})
export class MessageQueueDetailComponent implements OnInit {
  @Input() entityConfig!: EntityConfig;
  @Input() id!: string;

  private readonly svc = inject(MessageQueueService);
  private readonly location = inject(Location);
  private readonly router = inject(Router);
  readonly shellRoutes = inject(ShellRoutesService);

  readonly item = signal<MessageQueueItem | null>(null);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly resending = signal(false);
  readonly copyToast = signal(false);
  readonly showRequest = signal(false);
  readonly showConfirmation = signal(false);
  readonly openAttempts = signal<Set<number>>(new Set());
  private readonly sanitizer = inject(DomSanitizer);
  readonly previewOpen = signal(false);
  readonly previewMobile = signal(false);

  /** The stored email HTML, shown in a script-less sandboxed iframe (the sanitizer would strip its styles). */
  readonly previewHtml = computed<SafeHtml>(() =>
    this.sanitizer.bypassSecurityTrustHtml(this.item()?.message ?? ''),
  );

  canPreview(): boolean {
    const mq = this.item();
    return !!mq && mq.channel.value === 'EMAIL' && mq.application.value === 'HTML' && !!mq.message;
  }

  ngOnInit(): void {
    this.load();
  }

  goBack(): void {
    goBackOrNavigate(this.location, this.router, this.shellRoutes.commands('manage', this.entityConfig.key));
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.svc.getMessage(this.id).subscribe({
      next: (data) => {
        this.item.set(data);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err?.error?.detail ?? err?.message ?? 'Failed to load message.');
        this.loading.set(false);
      },
    });
  }

  canResend(): boolean {
    const status = this.item()?.status.value;
    return status === 'FAILED' || status === 'SENT';
  }

  resend(): void {
    this.resending.set(true);
    this.error.set(null);
    this.svc.resend(this.id).subscribe({
      next: (data) => {
        this.item.set(data);
        this.resending.set(false);
      },
      error: (err) => {
        this.error.set(err?.error?.error ?? err?.message ?? 'Resend failed.');
        this.resending.set(false);
      },
    });
  }

  statusVariant(status: string): UiBadgeVariant {
    const map: Record<string, UiBadgeVariant> = {
      PENDING: 'warning',
      PROCESSING: 'info',
      SENT: 'success',
      FAILED: 'danger',
      RETRYING: 'warning',
    };
    return map[status] ?? 'neutral';
  }

  deliveryVariant(status: string): UiBadgeVariant {
    if (status === 'DELIVERED') return 'success';
    if (status === 'UNDELIVERED' || status === 'REJECTED') return 'danger';
    return 'neutral';
  }

  toggleAttempt(index: number): void {
    this.openAttempts.update((set) => {
      const next = new Set(set);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }

  copy(value: string): void {
    navigator.clipboard.writeText(value).then(() => {
      this.copyToast.set(true);
      setTimeout(() => this.copyToast.set(false), 1500);
    });
  }

  isEmpty(value: unknown): boolean {
    if (Array.isArray(value)) return value.length === 0;
    return !value || Object.keys(value as object).length === 0;
  }

  formatJson(value: unknown): string {
    return JSON.stringify(value ?? {}, null, 2);
  }

  formatDate(value: string | null): string {
    if (!value) return '—';
    const d = new Date(value);
    return isNaN(d.getTime()) ? value : d.toLocaleString();
  }
}
